import apiClient, { ApiResponse } from './client';
import type {
  LangfuseDataset,
  LangfuseDatasetDetail,
  LangfuseDatasetItem,
  DatasetItemUpdateRequest,
  PersonaGenerateRequest,
  PersonaGenerateResponse,
  SimulationBatchCreateRequest,
  SimulationBatch,
  SimulationBatchListResponse,
  SimulationBatchRunRequest,
  SimulationBatchRunResponse,
} from '@/types/evaluator';

class EvaluatorService {
  async listDatasets(organizationId: string): Promise<ApiResponse<{ datasets: LangfuseDataset[]; total: number }>> {
    return apiClient.get('/api/evals/langfuse/datasets', { organization_id: organizationId });
  }

  async getDatasetDetail(name: string, organizationId: string): Promise<ApiResponse<LangfuseDatasetDetail>> {
    return apiClient.get(`/api/evals/langfuse/datasets/${encodeURIComponent(name)}`, { organization_id: organizationId });
  }

  async getDatasetItem(datasetName: string, itemId: string, organizationId: string): Promise<ApiResponse<LangfuseDatasetItem>> {
    return apiClient.get(
      `/api/evals/langfuse/datasets/${encodeURIComponent(datasetName)}/items/${encodeURIComponent(itemId)}`,
      { organization_id: organizationId },
    );
  }

  async updateDatasetItem(
    datasetName: string,
    itemId: string,
    organizationId: string,
    data: DatasetItemUpdateRequest,
  ): Promise<ApiResponse<LangfuseDatasetItem>> {
    return apiClient.patch(
      `/api/evals/langfuse/datasets/${encodeURIComponent(datasetName)}/items/${encodeURIComponent(itemId)}?organization_id=${encodeURIComponent(organizationId)}`,
      data,
    );
  }

  async deleteDatasetItem(
    datasetName: string,
    itemId: string,
    organizationId: string,
  ): Promise<ApiResponse<{ success: boolean; message: string; items_deleted: number }>> {
    return apiClient.delete(
      `/api/evals/langfuse/datasets/${encodeURIComponent(datasetName)}/items/${encodeURIComponent(itemId)}`,
      { organization_id: organizationId },
    );
  }

  async deleteDataset(
    name: string,
    organizationId: string,
  ): Promise<ApiResponse<{ success: boolean; message: string; items_deleted: number }>> {
    return apiClient.delete(
      `/api/evals/langfuse/datasets/${encodeURIComponent(name)}`,
      { organization_id: organizationId },
    );
  }

  async generatePersonas(data: PersonaGenerateRequest): Promise<ApiResponse<PersonaGenerateResponse>> {
    return apiClient.post('/api/evals/datasets/generate', data);
  }

  async listSimulationBatches(
    organizationId: string,
    limit = 50,
  ): Promise<ApiResponse<SimulationBatchListResponse>> {
    return apiClient.get('/api/evals/batches', { organization_id: organizationId, limit });
  }

  async createSimulationBatch(
    data: SimulationBatchCreateRequest,
  ): Promise<ApiResponse<SimulationBatch>> {
    return apiClient.post('/api/evals/batches', data);
  }

  async getSimulationBatch(
    batchId: string,
    organizationId: string,
  ): Promise<ApiResponse<SimulationBatch>> {
    return apiClient.get(`/api/evals/batches/${batchId}`, { organization_id: organizationId });
  }

  async retrySimulationBatch(
    batchId: string,
    organizationId: string,
  ): Promise<ApiResponse<SimulationBatchRunResponse>> {
    return this.runSimulationBatch(batchId, {
      organization_id: organizationId,
    });
  }

  async runSimulationBatch(
    batchId: string,
    data: SimulationBatchRunRequest,
  ): Promise<ApiResponse<SimulationBatchRunResponse>> {
    return apiClient.post(`/api/evals/batches/${batchId}/run`, data);
  }
}

const evaluatorService = new EvaluatorService();
export default evaluatorService;
